package one.devsbox.www.ui.campaigns

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.Crossfade
import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.GridItemSpan
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.rotate
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atStartOfDayIn
import one.devsbox.commons.EMPTY
import one.devsbox.shared.ads.CampaignState
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tk.mallumo.utils.noDiacriticsLower
import kotlin.time.Duration.Companion.milliseconds

@Composable
@ComposableNavNode
fun ListingCampaignsUI() {
    with(ListingCampaignsCtx.get()) {
        ContentListingCampaignsUI()
    }
}

@Composable
context(ctx: ListingCampaignsCtx)
private fun ContentListingCampaignsUI() {
    Box(Modifier.fillMaxSize()) {
        FullScreens()

        if (ctx.vm.state.entry?.count == 0
            && ctx.vm.selectedDate == LocalDate.EMPTY
            && ctx.vm.selectedStates.size == ctx.vm.defaultStates.size && ctx.vm.selectedStates.containsAll(
                ctx.vm.defaultStates
            )
        ) {
            CategorySplashUI(
                icon = Svg.CampaignOutline,
                title = Res.string.ui_campaigns_splash_title,
                explanation = Res.string.ui_campaigns_splash_desc,
                actions = ctx.vm.contentActions
            )
        } else {
            ContentComposition()
        }
    }
}

context(ctx: ListingCampaignsCtx) @Composable
private fun BoxScope.ContentComposition() {
    Column(Modifier.fillMaxSize()) {
        SearchBox(
            state = ctx.vm.search,
            hint = stringResource(Res.string.ui_campaigns_listing_search_placeholder),
            modifier = Modifier.fillMaxWidth()
                .padding(16.dp),
            actions = listOf(
                {
                    MultiStateFilterDropdown(
                        modifier = Modifier.align(Alignment.CenterVertically)
                            .width(210.dp),
                        selectedStates = ctx.vm.selectedStates,
                        onToggle = { ctx.action.toggleState(it) }
                    )
                }, {
                    DateFilterButton(
                        modifier = Modifier.align(Alignment.CenterVertically),
                        selectedDate = ctx.vm.selectedDate,
                        onDateSelected = { ctx.action.setDate(it) }
                    )
                }
            )
        )
        Crossfade(
            targetState = ctx.vm.state,
            modifier = Modifier.fillMaxSize()
        ) { state ->
            state.entry?.also {
                SuccessUI(it)
            }
        }
    }

    ContentActions(
        contentActions = ctx.vm.contentActions,
        colorInactive = ctx.colorSecondaryContainer,
        colorActive = ctx.colorPrimaryContainer
    )
}

context(ctx: ListingCampaignsCtx) @Composable
private fun FullScreens() {
    val state = ctx.vm.state
    if (state.entry == null) {
        Crossfade(targetState = state) { currentState ->
            when (currentState) {
                is DataState.Idle,
                is DataState.Loading -> FullscreenLoadingUI()

                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = currentState.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Unit
            }
        }
    }
}

@Composable
context(ctx: ListingCampaignsCtx)
private fun SuccessUI(data: SharedIO.Campaigns) {
    Column(modifier = Modifier.fillMaxSize()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(bottom = 16.dp)
                .padding(horizontal = 16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            val showingStart = if (data.items.isEmpty()) 0 else 1
            val showingEnd = data.items.size
            val totalCount = data.count

            Text(
                text = stringResource(
                    Res.string.ui_campaigns_listing_showing,
                    showingStart,
                    showingEnd,
                    totalCount
                ),
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f),
                letterSpacing = 1.sp,
                modifier = Modifier.weight(1f)
            )

            EntryViewSwitch(ctx.prefs.campaignLayoutType) {
                ctx.prefs.campaignLayoutType = it
            }
        }

        val viewMode = ctx.prefs.campaignLayoutType
        val filteredCampaigns = data.items.filter { entry ->
            ctx.vm.search.text.trim().let {
                it.isEmpty() || entry.name.noDiacriticsLower.contains(ctx.vm.search.text.toString().noDiacriticsLower)
            }
        }.sortedByDescending { it.id }

        AnimatedContent(targetState = viewMode, modifier = Modifier.weight(1f)) { mode ->
            if (mode == EntryViewMode.Card) {
                LazyVerticalGrid(
                    columns = GridCells.Adaptive(minSize = 240.dp),
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = PaddingValues(bottom = 250.dp, start = 16.dp, end = 16.dp),
                    horizontalArrangement = Arrangement.spacedBy(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    items(filteredCampaigns) { campaign ->
                        CampaignCard(campaign) {
                            ctx.action.openDetail(campaign.id)
                        }
                    }

                    if (data.items.size < data.count) {
                        item(span = { GridItemSpan(maxLineSpan) }) {
                            LoadMoreButton(
                                isLoading = ctx.vm.loadingMore,
                                onClick = { ctx.action.loadMore() }
                            )
                        }
                    }
                }
            } else {
                val dp = LocalDisplayProvider.current.state
                Column(modifier = Modifier.fillMaxSize()) {
                    if (!dp.smallScreen) {
                        CampaignTableHeader()
                    }

                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(bottom = 250.dp, start = 16.dp, end = 16.dp)
                    ) {
                        itemsIndexed(filteredCampaigns) { index, campaign ->
                            if (index != 0) {
                                if (dp.smallScreen) Spacer(Modifier.size(8.dp))
                                else HorizontalDivider(
                                    color = MaterialTheme.colorScheme.outlineVariant.copy(
                                        alpha = 0.5f
                                    )
                                )
                            }
                            CampaignRow(campaign) {
                                ctx.action.openDetail(campaign.id)
                            }
                        }

                        if (data.items.size < data.count) {
                            item {
                                LoadMoreButton(
                                    isLoading = ctx.vm.loadingMore,
                                    onClick = { ctx.action.loadMore() }
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
context(ctx: ListingCampaignsCtx)
private fun CampaignCard(campaign: SharedIO.CampaignSimple, onClick: () -> Unit) {
    val campaignState = CampaignState[campaign.state] ?: CampaignState.DRAFT
    Card(
        onClick = onClick,
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(4.dp),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = androidx.compose.foundation.BorderStroke(
            1.dp,
            MaterialTheme.colorScheme.outlineVariant
        )
    ) {
        Column {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .aspectRatio(16f / 9f)
                    .background(MaterialTheme.colorScheme.surfaceVariant),
                contentAlignment = Alignment.Center
            ) {
                if (campaign.media.isNotEmpty()) {
                    LazyImage(
                        id = campaign.media.first(),
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Crop
                    )
                } else {
                    Icon(
                        imageVector = Svg.CampaignOutline,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                        modifier = Modifier.size(36.dp)
                    )
                }

                // Top right corner state (only colored icon)

                Icon(
                    imageVector = campaignState.icon(),
                    contentDescription = campaignState.name,
                    tint = campaignState.color(),
                    modifier = Modifier
                        .align(Alignment.TopEnd)
                        .padding(8.dp)
                        .background(
                            color = MaterialTheme.colorScheme.surface.copy(alpha = 0.8f),
                            shape = CircleShape
                        )
                        .size(24.dp)
                )
            }

            Column(modifier = Modifier.padding(16.dp)) {
                // singleline name
                Text(
                    text = campaign.name.uppercase(),
                    fontWeight = FontWeight.Bold,
                    style = MaterialTheme.typography.bodyLarge,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Spacer(modifier = Modifier.height(4.dp))
                // second line interval
                val endText =
                    if (campaign.dEnd == LocalDate.EMPTY) stringResource(Res.string.ui_campaigns_edit_perpetual) else campaign.dEnd.toString()
                Text(
                    text = "${campaign.dStart} - $endText",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    fontFamily = FontFamily.Monospace,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )

                if (ctx.me.info.adminMode) {
                    Text(
                        text = campaign.nameCompany,
                        style = MaterialTheme.typography.labelMedium,
                        color = MaterialTheme.colorScheme.secondary,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    }
}


@Composable
private fun CampaignTableHeader() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 12.dp, horizontal = 40.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_visual),
            Modifier.width(80.dp)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_state),
            Modifier.width(120.dp)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_identity),
            Modifier.weight(1f)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_interval),
            Modifier.width(128.dp)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_reach),
            Modifier.width(80.dp)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_geo),
            Modifier.width(80.dp)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_node),
            Modifier.width(80.dp)
        )
        HeaderItem(
            stringResource(Res.string.ui_campaigns_listing_header_asset),
            Modifier.width(80.dp)
        )
    }
    HorizontalDivider(
        color = MaterialTheme.colorScheme.outlineVariant,
        modifier = Modifier.padding(horizontal = 16.dp)
    )
}

@Composable
private fun HeaderItem(text: String, modifier: Modifier) {
    Text(
        text = text,
        modifier = modifier,
        style = MaterialTheme.typography.labelMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        fontWeight = FontWeight.Bold,
        letterSpacing = 1.sp
    )
}

@Composable
context(ctx: ListingCampaignsCtx)
private fun CampaignRow(campaign: SharedIO.CampaignSimple, onClick: () -> Unit) {
    val dp = LocalDisplayProvider.current.state
    Surface(
        modifier = Modifier.fillMaxWidth(),
        color = Color.Transparent,
        onClick = onClick
    ) {
        if (dp.smallScreen) {
            val campaignState = CampaignState[campaign.state] ?: CampaignState.DRAFT
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(MaterialTheme.shapes.large)
                    .border(
                        BorderStroke(
                            0.5.dp,
                            MaterialTheme.colorScheme.outlineVariant.copy(alpha = 0.5f)
                        ), MaterialTheme.shapes.large
                    )
                    .padding(end = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(
                    modifier = Modifier
                        .size(64.dp)
                        .background(MaterialTheme.colorScheme.surfaceVariant),
                    contentAlignment = Alignment.Center
                ) {
                    if (campaign.media.isNotEmpty()) {
                        LazyImage(
                            id = campaign.media.first(),
                            modifier = Modifier.fillMaxSize(),
                            contentScale = ContentScale.Fit
                        )
                    } else {
                        Icon(
                            imageVector = Svg.CampaignOutline,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f),
                            modifier = Modifier.size(16.dp)
                        )
                    }
                }

                Spacer(Modifier.width(12.dp))

                // Name & Metadata stacked
                Column(modifier = Modifier.weight(1f)) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Text(
                            text = campaign.name.uppercase(),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurface,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f)
                        )
                    }
                    Spacer(modifier = Modifier.height(4.dp))
                    val endText =
                        if (campaign.dEnd == LocalDate.EMPTY) stringResource(Res.string.ui_campaigns_edit_perpetual) else campaign.dEnd.toString()
                    Text(
                        text = "${campaign.dStart} - $endText",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                    if (ctx.me.info.adminMode) {
                        Text(
                            text = campaign.nameCompany,
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.secondary,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        } else {
            Column {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 16.dp, horizontal = 16.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Visual
                    Box(
                        Modifier
                            .size(60.dp, 60.dp)
                            .border(
                                border = androidx.compose.foundation.BorderStroke(
                                    1.dp,
                                    MaterialTheme.colorScheme.outline
                                ),
                                shape = MaterialTheme.shapes.large

                            )
                            .clip(MaterialTheme.shapes.large)

                    ) {
                        if (campaign.media.isNotEmpty()) {
                            LazyImage(
                                id = campaign.media.first(),
                                contentScale = ContentScale.Crop,
                                alphaBackground = false,
                                modifier = Modifier.fillMaxSize()
                                    .clip(MaterialTheme.shapes.large)
                            )
                        }
                    }

                    Spacer(Modifier.width(20.dp))

                    // State
                    val campaignState = CampaignState[campaign.state] ?: CampaignState.DRAFT
                    StateLabel(campaignState, Modifier.width(120.dp))

                    // Identity
                    Column(modifier = Modifier.weight(1f)) {
                        Text(
                            text = campaign.name.uppercase(),
                            fontWeight = FontWeight.Bold,
                            style = MaterialTheme.typography.bodyLarge,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )
                        if (ctx.me.info.adminMode) {
                            Text(
                                text = campaign.nameCompany,
                                style = MaterialTheme.typography.labelMedium,
                                color = MaterialTheme.colorScheme.secondary,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }
                    }

                    // Interval
                    Column(Modifier.width(128.dp)) {
                        Text(
                            text = campaign.dStart.toString(),
                            style = MaterialTheme.typography.bodySmall,
                            fontFamily = FontFamily.Monospace
                        )
                        Text("-", style = MaterialTheme.typography.bodySmall)
                        Text(
                            text = if (campaign.dEnd == LocalDate.EMPTY) stringResource(Res.string.ui_campaigns_edit_perpetual) else campaign.dEnd.toString(),
                            style = MaterialTheme.typography.bodySmall,
                            color = if (campaign.dEnd == LocalDate.EMPTY) MaterialTheme.colorScheme.onSurfaceVariant.copy(
                                alpha = 0.5f
                            ) else Color.Unspecified,
                            fontFamily = FontFamily.Monospace
                        )
                    }

                    // Reach
                    Text(
                        text = campaign.impressions.all.formatCount(),
                        modifier = Modifier.width(80.dp),
                        color = MaterialTheme.colorScheme.primary,
                        fontWeight = FontWeight.Bold,
                        fontFamily = FontFamily.Monospace
                    )

                    // Geo
                    Text(
                        text = campaign.countLocation.toString(),
                        modifier = Modifier.width(80.dp),
                        fontFamily = FontFamily.Monospace
                    )

                    // Node
                    Text(
                        text = campaign.countDevice.toString(),
                        modifier = Modifier.width(80.dp),
                        fontFamily = FontFamily.Monospace
                    )

                    // Asset
                    Text(
                        text = campaign.media.size.toString(),
                        modifier = Modifier.width(80.dp),
                        fontFamily = FontFamily.Monospace
                    )
                }
            }
        }
    }
}

@Composable
fun CampaignState.icon() = remember(this) {
    when (this) {
        CampaignState.DRAFT -> Svg.PencilCircle
        CampaignState.ACTIVE -> Svg.PlayCircle
        CampaignState.PAUSED -> Svg.PauseCircle
        CampaignState.FINISHED -> Svg.CheckCircle
    }
}

@Composable
fun CampaignState.color() = when (this) {
    CampaignState.DRAFT -> MaterialTheme.colorScheme.secondary
    CampaignState.ACTIVE -> MaterialTheme.colorScheme.primary
    CampaignState.PAUSED -> MaterialTheme.colorScheme.tertiary
    CampaignState.FINISHED -> MaterialTheme.colorScheme.outline
}

@Composable
private fun StateLabel(state: CampaignState, modifier: Modifier) {


    Surface(
        modifier = modifier.padding(end = 16.dp),
        color = Color.Transparent,
        border = androidx.compose.foundation.BorderStroke(1.dp, state.color().copy(alpha = 0.5f)),
        shape = RoundedCornerShape(16.dp)
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.Center
        ) {
            Icon(
                imageVector = state.icon(),
                contentDescription = null,
                modifier = Modifier.size(24.dp),
                tint = state.color()
            )
            Spacer(Modifier.width(8.dp))
            Text(
                text = state.name,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Bold,
                color = state.color()
            )
        }
    }
}

@Composable
private fun MultiStateFilterDropdown(
    modifier: Modifier = Modifier,
    selectedStates: List<CampaignState>,
    onToggle: (CampaignState) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val rotation by animateFloatAsState(if (expanded) -90f else 90f)

    Box(modifier) {
        OutlinedButton(
            onClick = { expanded = true },
            shape = RoundedCornerShape(4.dp),
            contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
            modifier = Modifier.height(48.dp),
            border = androidx.compose.foundation.BorderStroke(0.5.dp, Color.Transparent)
        ) {
            Text(
                text = stringResource(Res.string.ui_campaigns_listing_filter_state_prefix),
                fontSize = 13.sp,
                color = MaterialTheme.colorScheme.onSurface
            )
            selectedStates.forEach { state ->
                Icon(
                    imageVector = state.icon(),
                    contentDescription = null,
                    modifier = Modifier.size(24.dp).padding(horizontal = 2.dp),
                    tint = state.color()
                )
            }
            Spacer(Modifier.weight(1F))
            Icon(
                imageVector = Svg.ChevronRight,
                contentDescription = null,
                modifier = Modifier.size(12.dp).rotate(rotation),
                tint = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }

        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            modifier = Modifier.background(MaterialTheme.colorScheme.surface)
        ) {
            CampaignState.entries.forEach { state ->
                DropdownMenuItem(
                    text = {
                        val color by animateColorAsState(
                            if (selectedStates.contains(state)) state.color()
                            else MaterialTheme.colorScheme.onBackground
                        )
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            Icon(
                                imageVector = state.icon(),
                                contentDescription = state.name,
                                modifier = Modifier.size(24.dp),
                                tint = color
                            )
                            Spacer(Modifier.width(8.dp))
                            Text(state.name, color = color)
                        }
                    },
                    onClick = { onToggle(state) }
                )
            }
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun DateFilterButton(
    modifier: Modifier = Modifier,
    selectedDate: LocalDate,
    onDateSelected: (LocalDate) -> Unit
) {
    var showPicker by remember { mutableStateOf(false) }

    OutlinedButton(
        onClick = { showPicker = true },
        shape = RoundedCornerShape(4.dp),
        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 8.dp),
        modifier = modifier.height(48.dp),
        border = androidx.compose.foundation.BorderStroke(0.5.dp, Color.Transparent)
    ) {
        Text(
            text = if (selectedDate == LocalDate.EMPTY) stringResource(Res.string.ui_campaigns_listing_filter_date_any) else stringResource(
                Res.string.ui_campaigns_listing_filter_date_format,
                selectedDate.toString()
            ),
            fontSize = 13.sp,
            color = MaterialTheme.colorScheme.onSurface
        )
        Spacer(Modifier.width(8.dp))
        Icon(
            imageVector = Svg.CalendarRangeOutline,
            contentDescription = null,
            modifier = Modifier.size(16.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }

    if (showPicker) {
        val datePickerState = rememberDatePickerState(
            initialSelectedDateMillis = if (selectedDate == LocalDate.EMPTY) null else selectedDate.atStartOfDayIn(
                TimeZone.UTC
            ).toEpochMilliseconds()
        )

        DatePickerDialog(
            onDismissRequest = { showPicker = false },
            confirmButton = {
                TextButton(onClick = {
                    datePickerState.selectedDateMillis?.milliseconds?.inWholeDays?.also {
                        onDateSelected(LocalDate.fromEpochDays(it))
                    }
                    showPicker = false
                }) {
                    Text(stringResource(Res.string.ui_campaigns_listing_btn_ok))
                }
            },
            dismissButton = {
                TextButton(onClick = {
                    onDateSelected(LocalDate.EMPTY)
                    showPicker = false
                }) {
                    Text(stringResource(Res.string.ui_campaigns_listing_btn_reset))
                }
            }
        ) {
            DatePicker(state = datePickerState, showModeToggle = false)
        }
    }
}

@Composable
private fun LoadMoreButton(isLoading: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(vertical = 32.dp),
        contentAlignment = Alignment.Center
    ) {
        if (isLoading) {
            CircularProgressIndicator(modifier = Modifier.size(24.dp))
        } else {
            OutlinedButton(
                onClick = onClick,
                shape = RoundedCornerShape(4.dp),
                colors = ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.primary),
                border = androidx.compose.foundation.BorderStroke(
                    1.dp,
                    MaterialTheme.colorScheme.outlineVariant
                ),
                modifier = Modifier.width(200.dp).height(48.dp)
            ) {
                Text(
                    stringResource(Res.string.ui_users_listing_btn_load_more),
                    fontWeight = FontWeight.Bold
                )
            }
        }
    }
}

private fun Long.formatCount(): String {
    return when {
        this >= 1_000_000 -> "${(this / 1_000_000.0).let { (it * 10).toInt() / 10.0 }}M"
        this >= 1_000 -> "${(this / 1_000.0).let { (it * 10).toInt() / 10.0 }}K"
        else -> this.toString()
    }
}